import { useRef, useState } from 'react';
import {
  ChevronRight,
  CircleCheckBig,
  CircleX,
  List,
  Send,
  Trash2,
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import {
  useGetCounterProposalQuery,
  useUpdateCounterProposalMutation,
} from '@/apis/extended/negotiation';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import {
  ClauseStatusData,
  ProposalData,
  RemoveClauseStatusData,
  RemoveProposalData,
  RemoveSubsetClauseStatusData,
  SetClauseStatusData,
  SetProposalData,
  SetSubsetClauseStatusData,
  SubsetClauseStatusData,
} from '@/hooks/usestoragedata';
import { CounterProposalState } from '@/types/NegotiationType';
import {
  ClauseStatus,
  ContractClauseStatus,
  ContractStatus,
} from '@/types/StatusType';
import ContractClauseSelectStatus from '../Layout/SellerAgent/Negotiation/Component/ClauseStatus';
import ContractOverallStatus from '../Layout/SellerAgent/Negotiation/Component/ContractOverallStatus';
import NegotiationHeader from '../Layout/SellerAgent/Negotiation/Component/Negotiationheader';
import NegotiationStatusSummary from '../Layout/SellerAgent/Negotiation/Component/NegotiationSummary';
import { ClauseStatusBadge } from '../Layout/SellerAgent/Negotiation/Component/statusbadge';
import { useNegotiationClauses } from './components/negotiationclauses';

const BuyerNegotiationClauses = () => {
  const { contractId } = useParams();
  const navigate = useNavigate();

  const contractClauses = useNegotiationClauses(contractId ?? '');
  const [updateCounterProposal, { isLoading: isUpdating }] =
    useUpdateCounterProposalMutation();
  const { data: counterProposal } = useGetCounterProposalQuery(
    contractId ?? '',
    {
      skip: !contractId,
      refetchOnMountOrArgChange: false,
      refetchOnFocus: false,
      pollingInterval: 0,
    }
  );

  const [selectedSection, setSelectedSection] = useState(0);
  const [showTableOfContents, setShowTableOfContents] = useState(true);
  //contract overall status
  const [contractOverallStatus, setContractOverallStatus] =
    useState<ContractStatus>('voided');
  const [clauseStatuses, setClauseStatuses] = useState<{
    [key: number]: ClauseStatus;
  }>({});
  const [clauseSubsetStatus, setClauseSubsetStatus] = useState<{
    [key: number]: ContractClauseStatus;
  }>({});
  const [counterProposals, setCounterProposals] =
    useState<CounterProposalState>({});

  const role = localStorage.getItem('role');

  const cardRefs = useRef<HTMLDivElement[]>([]);
  const signatureRef = useRef<HTMLDivElement>(null);

  const handleTOCClick = (index: number) => {
    setSelectedSection(index);
    if (index < contractClauses.length) {
      cardRefs.current[index]?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    } else {
      signatureRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  };

  const handleClauseStatusChange = (index: number, status: ClauseStatus) => {
    setClauseStatuses((prev) => ({
      ...prev,
      [index]: status,
    }));

    if (status === 'not agreeable' && !counterProposals[index]) {
      setCounterProposals((prev) => ({
        ...prev,
        [index]: {
          text: contractClauses[index].description,
          status: 'draft',
          originalText: contractClauses[index].description,
          offeredPrice: '',
          earnestMoney: '',
          closingDate: '',
          homeInspection: '',
          financingContingency: '',
        },
      }));
    }
  };

  const SaveCounterProposal = (
    title: string,
    value: string,
    status?: ClauseStatus
  ) => {
    try {
      SetProposalData(title, value);
      if (status) return SetClauseStatusData(title, status);
      toast.success(`${title} Counter Proposal Added`);
    } catch (error) {
      toast.error('Failed to save counter proposal');
    }
  };

  const SaveClauseStatus = (title: string, status: ClauseStatus) => {
    try {
      SetClauseStatusData(title, status);
      if (status === 'agreeable')
        return SetProposalData(title, 'Agreed to original terms');
      toast.success(`${title} status set to ${status}`);
    } catch (error) {
      toast.error('Failed to save clause status');
    }
  };
  const SaveSubsetOfClauseState = (
    title: string,
    status: ContractClauseStatus
  ) => {
    try {
      if (status === null || status === undefined) {
        toast.error('Please select a  Clause Status');
      }
      SetSubsetClauseStatusData(title, status);
    } catch (error) {
      toast.error('Failed to save clause status');
    }
  };
  const updateAllCounterProposals = async () => {
    try {
      const getFieldData = (title: string) => {
        const value = ProposalData(title) || '';
        const status = ClauseStatusData(title) || '';
        const subsetClauseStatus = SubsetClauseStatusData(title) || '';
        return { value, status, subsetClauseStatus };
      };

      const allClauseData: {
        [key: string]: {
          value: string;
          status: string;
          subsetClauseStatus: string;
        };
      } = {};
      Object.keys(localStorage).forEach((key) => {
        if (key.startsWith('counter:')) {
          const clauseTitle = key.replace('counter:', '');
          const value = ProposalData(clauseTitle) || '';
          const status = ClauseStatusData(clauseTitle) || '';
          const subsetClauseStatus = SubsetClauseStatusData(clauseTitle) || '';
          allClauseData[clauseTitle] = { value, status, subsetClauseStatus };
        }
      });

      await updateCounterProposal({
        contractId: contractId ?? '',
        offeredPrice: getFieldData('Offered Price'),
        EarnestMoney: getFieldData('Earnest Money Deposit'),
        ClosingDate: getFieldData('Closing Date'),
        AppraisalContingency: getFieldData('Appraisal Contingency'),
        HomeInspection: getFieldData('Home Inspection Contingency'),
        FinancingContingency: getFieldData('Financing Contingency'),
        postCloseOccupancy: getFieldData('Post Close Occupancy'),
        proposedBy: { value: role ?? '', status: '', subsetClauseStatus: '' },
        NegotiationStatus: {
          value: contractOverallStatus,
          status: '',
          subsetClauseStatus: '',
        },
        Suggested: getFieldData('Suggested'),
        Contingent: getFieldData('Contingent'),
        additionalClauses: allClauseData,
      }).unwrap();

      Object.keys(localStorage).forEach((key) => {
        if (
          key.startsWith('counter:') ||
          key.startsWith('clauseStatus:') ||
          key.startsWith('subsetClauseStatus:')
        ) {
          RemoveProposalData(key);
          RemoveClauseStatusData(key);
          RemoveSubsetClauseStatusData(key);
        }
      });

      navigate(`/agent/counter-proposal/${contractId}`);
      toast.success('Counter proposals updated');
    } catch (error) {
      console.error('Failed to update counter proposal:', error);
      toast.error('Failed to update counter proposal');
    }
  };

  return (
    <div className="relative flex h-screen bg-[var(--surface)] text-[var(--on-surface)]">
      {showTableOfContents && (
        <div
          className="fixed inset-0 z-20 bg-black bg-opacity-30 md:hidden"
          onClick={() => setShowTableOfContents(true)}
        />
      )}
      {showTableOfContents && (
        <>
          <div className="fixed left-0 top-0 z-30 flex h-full w-72 gap-4 overflow-y-auto scroll-auto bg-[var(--surface-container-low)] p-6 transition-transform duration-300 md:static md:block md:w-1/4 md:translate-x-0">
            <div className="mb-2 flex items-center justify-between">
              <h2 className="font-display mb-2 text-xl font-bold text-[var(--on-surface)]">
                Table of Contents
              </h2>
              <Button
                variant="ghost"
                size="icon"
                className="md:hidden"
                onClick={() => setShowTableOfContents(false)}
                aria-label="Close sidebar"
              >
                ×
              </Button>
            </div>

            <p className="mb-4 text-sm text-[var(--on-surface-variant)]">
              Select a section to view details
            </p>
            <div className="space-y-2">
              {contractClauses.map((review, index) => (
                <div
                  key={index}
                  onClick={() => handleTOCClick(index)}
                  className={`w-full cursor-pointer rounded-md bg-[var(--surface-container-lowest)] p-3 text-left transition-colors duration-200 ${
                    selectedSection === index
                      ? 'ring-2 ring-[rgba(0,8,30,0.2)]'
                      : 'hover:bg-[var(--surface-container-low)]'
                  } text-[var(--on-surface)]`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="flex justify-between font-medium text-[var(--on-surface)]">
                        {review.title}
                      </h3>
                      <p className="mt-1 line-clamp-2 text-sm text-[var(--on-surface-variant)]">
                        {review.description.length > 80
                          ? `${review.description.substring(0, 80)}...`
                          : review.description}
                      </p>
                      <p className="mt-1 line-clamp-2 text-sm text-[var(--on-surface-variant)]">
                        <div className="flex gap-1 flex-wrap justify-end">
                          {clauseSubsetStatus[index] &&
                            ClauseStatusBadge(
                              clauseSubsetStatus[index] as ContractClauseStatus
                            )}
                        </div>
                      </p>
                    </div>
                    <ChevronRight className="h-4 w-4 text-[var(--on-surface-variant)]" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
      {/* Right Side - Content Area */}
      <div className="relative flex-1 overflow-y-auto bg-[var(--surface)] p-6 text-[var(--on-surface)]">
        <div className="flex justify-end mb-4 sticky top-0 z-10 ">
          <Button
            variant="outline"
            size="sm"
            className="flex items-center gap-2 bg-[var(--surface-container-lowest)] text-[var(--on-surface-variant)]"
            onClick={() => setShowTableOfContents(!showTableOfContents)}
          >
            <List className="h-4 w-4" />
            Contents
          </Button>
        </div>
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <NegotiationHeader />

          {/* Contract Summary */}
          {/* <NegotiationStatusSummary /> */}

          {/* Scrool to sign button */}
          <div className="flex justify-end mb-4 bottom-2 right-16 gap-2 ">
            <ContractOverallStatus
              setContractStatus={contractOverallStatus}
              updateStatus={setContractOverallStatus}
            />
          </div>
          {/* Clause Cards */}
          <div className="space-y-6">
            {contractClauses.map((review, index) => (
              <div
                key={index}
                ref={(el) => {
                  if (el) {
                    cardRefs.current[index] = el;
                  }
                }}
                className="mb-8"
              >
                <Card className="bg-[var(--surface-container-lowest)] shadow-none transition-shadow duration-200 hover:shadow-ambient">
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <CardTitle className="text-xl font-semibold capitalize leading-tight text-[var(--on-surface)]">
                        {review.title}
                      </CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-6">
                      <div className="leading-relaxed text-[var(--on-surface)]">
                        {review.description}
                      </div>
                      <Separator className="my-2" />

                      <div className="space-y-4">
                        <div className="flex items-center text-sm font-medium text-[var(--on-surface-variant)]">
                          Your Response:
                        </div>
                        <div className="flex gap-3">
                          <Button
                            variant="outline"
                            onClick={() => {
                              handleClauseStatusChange(index, 'agreeable');
                              setClauseSubsetStatus((prev) => ({
                                ...prev,
                                [index]: 'agreeable' as ContractClauseStatus,
                              }));
                              SaveClauseStatus(review.title, 'agreeable');
                              SaveSubsetOfClauseState(
                                review.title,
                                'agreeable'
                              );
                            }}
                            className={`flex items-center gap-2 ${
                              clauseStatuses[index] === 'agreeable'
                                ? 'bg-[var(--primary-navy)] text-white'
                                : 'text-[var(--on-surface)] hover:bg-[color-mix(in_srgb,var(--primary-navy)_10%,transparent)]'
                            }`}
                          >
                            <CircleCheckBig className="h-4 w-4 text-green-500" />
                            Agreeable
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => {
                              handleClauseStatusChange(index, 'not agreeable');
                              setClauseSubsetStatus((prev) => ({
                                ...prev,
                                [index]: 'draft' as ContractClauseStatus,
                              }));
                              SaveClauseStatus(review.title, 'not agreeable');
                              SaveSubsetOfClauseState(review.title, 'draft');
                            }}
                            className={`flex cursor-pointer items-center gap-2 bg-[var(--surface-container-lowest)] text-[var(--on-surface)] ${
                              clauseStatuses[index] === 'not agreeable'
                                ? 'border border-red-500 bg-red-500/20 text-red-800 hover:bg-red-500/10'
                                : 'hover:bg-red-500/10'
                            }`}
                          >
                            <CircleX className="h-4 w-4 text-red-500" />
                            Not Agreeable
                          </Button>
                        </div>
                      </div>
                      {clauseStatuses[index] === 'not agreeable' && (
                        <div className="mt-6 p-6 bg-blue-50 rounded-lg border border-blue-200">
                          <div className="flex flex-row justify-end">
                            <ContractClauseSelectStatus
                              setClauseStatus={
                                clauseSubsetStatus[index] ?? 'draft'
                              }
                              updateStatus={(status) =>
                                setClauseSubsetStatus((prev) => ({
                                  ...prev,
                                  [index]: status,
                                }))
                              }
                            />
                          </div>
                          <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-900 mb-2">
                              Your Counter Proposal
                            </label>
                            <p className="text-xs text-gray-600 mb-3">
                              Provide your proposed changes to this clause
                            </p>
                          </div>
                          <Textarea
                            value={
                              counterProposals[index]?.text ||
                              review.description
                            }
                            onChange={(e) => {
                              setCounterProposals((prev) => ({
                                ...prev,
                                [index]: {
                                  ...prev[index],
                                  text: e.target.value,
                                },
                              }));
                            }}
                            placeholder="Describe your proposed changes to this clause..."
                            rows={6}
                            className="w-full bg-white border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                          />

                          {counterProposals[index]?.status === 'submitted' && (
                            <div className="mt-3 text-sm text-blue-600">
                              Submitted on{' '}
                              {counterProposals[
                                index
                              ]?.submittedAt?.toLocaleDateString()}
                            </div>
                          )}

                          <div className="flex items-center justify-between mt-4">
                            <div className="text-xs text-gray-500">
                              Changes will be saved to your counter proposal
                            </div>
                            <div className="flex gap-2">
                              <Button
                                onClick={() => {
                                  SaveSubsetOfClauseState(
                                    review.title,
                                    clauseSubsetStatus[index]
                                  );
                                  SaveClauseStatus(
                                    review.title,
                                    'not agreeable'
                                  );
                                  SaveCounterProposal(
                                    review.title,
                                    counterProposals[index]?.text ||
                                      review.description,
                                    'not agreeable'
                                  );
                                }}
                                variant="outline"
                                size="sm"
                                className="bg-[#043a93] text-white hover:bg-white border-[#043a93] hover:text-[#043a93] cursor-pointer"
                              >
                                <Send className="h-4 w-4 mr-2" />
                                {ProposalData(review.title)
                                  ? 'Added'
                                  : 'Add ' + review.title}
                              </Button>

                              <Button
                                onClick={() => {
                                  RemoveProposalData(review.title);
                                  RemoveClauseStatusData(review.title);
                                  RemoveSubsetClauseStatusData(review.title);
                                  setCounterProposals((prev) => {
                                    const updated = { ...prev };
                                    delete updated[index];
                                    return updated;
                                  });
                                  setClauseStatuses((prev) => {
                                    const updated = { ...prev };
                                    delete updated[index];
                                    return updated;
                                  });
                                  setClauseSubsetStatus((prev) => {
                                    const updated = { ...prev };
                                    delete updated[index];
                                    return updated;
                                  });
                                  toast.success(
                                    `${review.title} Counter Proposal Removed`
                                  );
                                }}
                                variant="outline"
                                size="sm"
                                className="text-red-600 hover:bg-red-50 hover:border-red-200"
                              >
                                <Trash2 /> Remove Counter Proposal
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            ))}

            {/* Submit Actions */}
            {counterProposal?.negotiation?.contractId && (
              <div className="mt-12 p-6 bg-gray-50 rounded-lg border border-gray-200">
                <div className="text-center">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Ready to Submit?
                  </h3>
                  <p className="text-sm text-gray-600 mb-6">
                    Review all your responses and counter proposals before
                    submitting
                  </p>
                  <Button
                    className="w-full max-w-md bg-primary text-white hover:bg-primary/90"
                    onClick={updateAllCounterProposals}
                    disabled={isUpdating}
                    size="lg"
                  >
                    {isUpdating ? (
                      <>
                        <div className="animate-spin h-4 w-4 mr-2 border-2 border-white border-t-transparent rounded-full" />
                        Updating...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Update Counter Proposals
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BuyerNegotiationClauses;
